import React, { useState, useEffect } from 'react';
import { Shield, Globe, Lock, Check, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SSOSettings = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [domainInput, setDomainInput] = useState('');

  useEffect(() => { fetchConfig(); }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${API_URL}/api/settings/sso`, { credentials: 'include' });
      const data = await res.json();
      setConfig(data);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const saveConfig = async (updates) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/settings/sso`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...config, ...updates }),
      });
      const data = await res.json();
      if (res.ok) {
        setConfig(data.sso_config || { ...config, ...updates });
        setMessage({ type: 'success', text: 'SSO configuration saved' });
      } else {
        setMessage({ type: 'error', text: data.detail || 'Failed to save' });
      }
    } catch (e) { setMessage({ type: 'error', text: 'Network error' }); }
    setSaving(false);
  };

  const toggleProvider = (provider) => {
    const providers = config?.providers || [];
    const updated = providers.includes(provider)
      ? providers.filter(p => p !== provider)
      : [...providers, provider];
    saveConfig({ providers: updated });
  };

  const addDomain = () => {
    if (!domainInput.trim()) return;
    const domains = [...(config?.allowed_domains || []), domainInput.trim().toLowerCase()];
    saveConfig({ allowed_domains: domains });
    setDomainInput('');
  };

  const removeDomain = (domain) => {
    const domains = (config?.allowed_domains || []).filter(d => d !== domain);
    saveConfig({ allowed_domains: domains });
  };

  if (loading) return <div style={{ padding: '1rem', color: 'hsl(var(--muted-foreground))' }}>Loading...</div>;

  const providers = [
    { id: 'google', name: 'Google', icon: Globe, color: '#4285f4' },
    { id: 'github', name: 'GitHub', icon: Shield, color: '#333' },
  ];

  return (
    <div data-testid="sso-settings">
      {message && (
        <div style={{
          padding: '0.5rem 0.75rem', marginBottom: '0.75rem', borderRadius: '0.375rem', fontSize: '0.8125rem',
          background: message.type === 'success' ? 'hsl(142 70% 15%)' : 'hsl(0 70% 15%)',
          color: message.type === 'success' ? 'hsl(142 70% 70%)' : 'hsl(0 70% 70%)',
          display: 'flex', alignItems: 'center', gap: '0.5rem',
        }}>
          <AlertCircle size={14} /> {message.text}
          <button onClick={() => setMessage(null)} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'inherit', cursor: 'pointer' }}>x</button>
        </div>
      )}

      {/* SSO Toggle */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', padding: '0.75rem', background: 'hsl(var(--secondary))', borderRadius: '0.375rem' }}>
        <div>
          <p style={{ fontSize: '0.875rem', fontWeight: 600 }}>Single Sign-On</p>
          <p style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>Enforce SSO for all team members</p>
        </div>
        <button
          data-testid="sso-toggle"
          onClick={() => saveConfig({ enabled: !config?.enabled })}
          disabled={saving}
          style={{
            width: '40px', height: '22px', borderRadius: '11px', border: 'none', cursor: 'pointer',
            background: config?.enabled ? 'hsl(var(--accent))' : 'hsl(var(--border))',
            position: 'relative', transition: 'background 0.2s',
          }}
        >
          <div style={{
            width: '18px', height: '18px', borderRadius: '50%', background: 'white',
            position: 'absolute', top: '2px', transition: 'left 0.2s',
            left: config?.enabled ? '20px' : '2px',
          }} />
        </button>
      </div>

      {/* Providers */}
      <p style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.5rem' }}>Identity Providers</p>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        {providers.map(p => {
          const active = config?.providers?.includes(p.id);
          return (
            <button
              key={p.id}
              data-testid={`sso-provider-${p.id}`}
              onClick={() => toggleProvider(p.id)}
              style={{
                flex: 1, padding: '0.625rem', borderRadius: '0.375rem', cursor: 'pointer',
                border: `1px solid ${active ? 'hsl(var(--accent))' : 'hsl(var(--border))'}`,
                background: active ? 'hsl(var(--accent) / 0.1)' : 'transparent',
                display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8125rem',
                color: 'hsl(var(--foreground))',
              }}
            >
              <p.icon size={16} style={{ color: p.color }} />
              {p.name}
              {active && <Check size={14} style={{ marginLeft: 'auto', color: 'hsl(var(--accent))' }} />}
            </button>
          );
        })}
      </div>

      {/* Enforce SSO */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.625rem', background: 'hsl(var(--secondary))', borderRadius: '0.375rem', marginBottom: '1rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Lock size={14} style={{ color: 'hsl(var(--muted-foreground))' }} />
          <span style={{ fontSize: '0.8125rem' }}>Enforce SSO (block email/password login)</span>
        </div>
        <button
          data-testid="sso-enforce-toggle"
          onClick={() => saveConfig({ enforced: !config?.enforced })}
          style={{
            width: '36px', height: '20px', borderRadius: '10px', border: 'none', cursor: 'pointer',
            background: config?.enforced ? '#f59e0b' : 'hsl(var(--border))',
            position: 'relative',
          }}
        >
          <div style={{
            width: '16px', height: '16px', borderRadius: '50%', background: 'white',
            position: 'absolute', top: '2px',
            left: config?.enforced ? '18px' : '2px',
          }} />
        </button>
      </div>

      {/* Domain Restriction */}
      <p style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.5rem' }}>Allowed Email Domains</p>
      <div style={{ display: 'flex', gap: '0.375rem', marginBottom: '0.5rem' }}>
        <input
          data-testid="sso-domain-input"
          value={domainInput} onChange={e => setDomainInput(e.target.value)}
          placeholder="e.g. company.com" onKeyDown={e => e.key === 'Enter' && addDomain()}
          style={{ flex: 1, padding: '0.5rem', borderRadius: '0.375rem', border: '1px solid hsl(var(--border))', background: 'hsl(var(--background))', color: 'hsl(var(--foreground))', fontSize: '0.8125rem' }}
        />
        <button onClick={addDomain} style={{
          padding: '0.5rem 0.75rem', borderRadius: '0.375rem', border: 'none', cursor: 'pointer',
          background: 'hsl(var(--accent))', color: 'hsl(var(--accent-foreground))', fontSize: '0.8125rem',
        }}>Add</button>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
        {(config?.allowed_domains || []).map(d => (
          <span key={d} style={{
            padding: '0.25rem 0.5rem', borderRadius: '0.25rem', fontSize: '0.75rem',
            background: 'hsl(var(--secondary))', display: 'flex', alignItems: 'center', gap: '0.25rem',
          }}>
            {d}
            <button onClick={() => removeDomain(d)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--muted-foreground))', fontSize: '0.75rem' }}>x</button>
          </span>
        ))}
        {(config?.allowed_domains || []).length === 0 && (
          <span style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))' }}>No domain restrictions (all domains allowed)</span>
        )}
      </div>
    </div>
  );
};

export default SSOSettings;
